import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { releaseVaultedMoney } from 'redux/modules/payments';
import { setSuccessMessage } from 'redux/modules/successMessage';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from 'react-bootstrap';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import PaymentModal from 'components/Pitch/PaymentModal';
import styles from './PaymentsTab.module.scss';

const PaymentsTab = ({
  payments,
  stats,
  freelancerName,
  loading,
  brief,
  pitch,
  onLoadPayments,
}) => {
  const [expandedPayments, setExpandedPayments] = useState(new Set());
  const [showReleasePaymentModal, setShowReleasePaymentModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const currencies = {
    usd: '$',
    gbp: '£',
    eur: '€',
  };

  const toggleBreakdown = (paymentId) => {
    setExpandedPayments((prev) => {
      const next = new Set(prev);
      if (next.has(paymentId)) {
        next.delete(paymentId);
      } else {
        next.add(paymentId);
      }
      return next;
    });
  };

  const formatCurrency = (amount, currencyCode = 'usd') => {
    const value = amount / 100; // Convert cents to dollars
    const symbol = currencies[currencyCode.toLowerCase()] || '$';
    return `${symbol}${value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getPaymentType = (fromStatus) => {
    if (fromStatus === 'outstanding') return 'Bill';
    return 'Payment';
  };

  const getPaymentStatus = (payment) => {
    const { from_transaction_status, to_transaction_status } = payment;

    // Check if payment is held (success but vaulted)
    if (
      from_transaction_status === 'success' &&
      to_transaction_status === 'vaulted'
    ) {
      return 'held';
    }

    // Map transaction status to display status
    const statusMap = {
      success: 'complete',
      failed: 'failed',
      outstanding: 'payment-due',
      refunded: 'refunded',
    };

    return statusMap[from_transaction_status] || 'pending';
  };

  const getStatusLabel = (status) => {
    const labelMap = {
      complete: 'Complete',
      'payment-due': 'Payment Due',
      held: 'In Vault',
      failed: 'Failed',
      refunded: 'Refunded',
      pending: 'Pending',
    };

    return labelMap[status] || 'Pending';
  };

  const releaseClientsVaultedMoney = (pitchId) => {
    setShowReleasePaymentModal(false);

    dispatch(releaseVaultedMoney(pitchId))
      .then(() => {
        dispatch(
          setSuccessMessage('Your money has been released to the freelancer.')
        );
        // Reload payments to reflect the changes
        if (onLoadPayments) {
          onLoadPayments(pitchId);
        }
      })
      .catch((err) => {
        alert(`Failed to release money to the freelancer: ${err.message}`);
      });
  };

  const adhocPayment = (briefId, pitchId, grandTotal) => {
    history.push(
      `/manage/briefs/${briefId}/pay/${pitchId}?amount=${grandTotal}&vaultable=true`
    );
  };

  const handleMakePayment = (paymentId, briefId, pitchId) => {
    // Find the outstanding bills for this payment
    const outstandingPayments = payments.filter(
      (p) => p.from_transaction_status === 'outstanding'
    );

    let billsQueryString = '?bills=';
    outstandingPayments.forEach((payment) => {
      billsQueryString += `${payment.id},`;
    });
    billsQueryString = billsQueryString.substring(
      0,
      billsQueryString.length - 1
    );

    history.push(`/manage/briefs/${briefId}/pay/${pitchId}${billsQueryString}`);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Loader loading />
      </div>
    );
  }

  // Calculate vault balance
  const totalVaulted = stats?.total_vaulted || 0;
  const isMoneyVaulted = totalVaulted > 0;

  const displayOrUsername = freelancerName || 'freelancer';

  return (
    <div className={styles.paymentsContainer}>
      {/* Payment Action Buttons */}
      <div className={styles.actionButtons}>
        <Button
          bsStyle={isMoneyVaulted ? 'default' : 'primary'}
          className={`${styles.buttons} ${
            isMoneyVaulted ? styles.defaultButton : ''
          }`}
          onClick={() => setShowPayModal(true)}
        >
          Make New Payment
        </Button>
        {isMoneyVaulted && (
          <Button
            bsStyle="primary"
            className={styles.buttons}
            onClick={() => setShowReleasePaymentModal(true)}
          >
            Release from Vault
          </Button>
        )}
      </div>

      {/* Summary Stats Section */}
      {stats && (
        <div className={styles.summarySection}>
          <div className={styles.summaryGrid}>
            {/* Total Paid Card */}
            <div className={styles.statCard}>
              <div className={styles.statLabel}>
                Total Paid to {freelancerName || 'Freelancer'}
              </div>
              <div className={styles.statValue}>
                {formatCurrency(
                  stats.total_earnings,
                  payments[0]?.from_currency_code
                )}
              </div>
            </div>

            {/* Vault Balance Card */}
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Vault Balance</div>
              <div className={styles.statValue}>
                {formatCurrency(
                  stats.total_vaulted,
                  payments[0]?.from_currency_code
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {payments.length > 0 && (
        <>
          <h2 className={styles.title}>Bills and Payments</h2>
          <div className={styles.paymentsList}>
            {payments.map((payment) => {
              const status = getPaymentStatus(payment);
              const isExpanded = expandedPayments.has(payment.id);

              return (
                <div key={payment.id} className={styles.paymentCard}>
                  {/* Header: Title and Status */}
                  <div className={styles.header}>
                    <div className={styles.titleSection}>
                      <h3 className={styles.paymentType}>
                        {getPaymentType(payment.from_transaction_status)}
                      </h3>
                    </div>
                    <div className={styles.statusSection}>
                      {status === 'payment-due' && (
                        <Button
                          bsStyle="primary"
                          className={styles.payBillButton}
                          onClick={() =>
                            handleMakePayment(payment.id, brief?.id, pitch?.id)
                          }
                        >
                          Make Payment
                        </Button>
                      )}
                      <span
                        className={`${styles.statusBadge} ${
                          status === 'complete'
                            ? styles.statusComplete
                            : status === 'payment-due'
                            ? styles.statusDue
                            : ''
                        }`}
                      >
                        {getStatusLabel(status)}
                      </span>
                    </div>
                  </div>

                  {/* Date and Amount Row */}
                  <div className={styles.amountRow}>
                    <div className={styles.date}>
                      {moment(payment.timestamp).format('MMM D, YYYY')}
                    </div>
                    <button
                      onClick={() => toggleBreakdown(payment.id)}
                      className={styles.amountButton}
                    >
                      <div className={styles.totalAmount}>
                        {formatCurrency(
                          payment.from_amount,
                          payment.from_currency_code
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={20} className={styles.chevron} />
                      ) : (
                        <ChevronDown size={20} className={styles.chevron} />
                      )}
                    </button>
                  </div>

                  {/* Collapsible Payment Breakdown */}
                  {isExpanded && (
                    <div className={styles.breakdown}>
                      <div className={styles.breakdownContent}>
                        <div className={styles.breakdownGrid}>
                          <span className={styles.breakdownLabel}>
                            Paid to freelancer:
                          </span>
                          <span className={styles.breakdownValue}>
                            {formatCurrency(
                              payment.to_amount,
                              payment.to_currency_code
                            )}
                          </span>
                          <span className={styles.breakdownLabel}>
                            Twine fees:
                          </span>
                          <span className={styles.breakdownValue}>
                            {formatCurrency(
                              payment.from_twine_fee,
                              payment.from_currency_code
                            )}
                          </span>
                          <span className={styles.breakdownLabel}>
                            Card Processing Fees:
                          </span>
                          <span className={styles.breakdownValue}>
                            {formatCurrency(
                              payment.from_card_fee,
                              payment.from_currency_code
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Release Vaulted Money Modal */}
      <Modal
        closeButton
        show={showReleasePaymentModal}
        title={`Release payment to ${displayOrUsername}`}
        onExit={() => setShowReleasePaymentModal(false)}
      >
        <PaymentModal
          skipProjectTermsStep
          brief={brief}
          pitch={pitch}
          totalVaulted={totalVaulted}
          readOnlyAmount
          onAccept={() => releaseClientsVaultedMoney(pitch?.id)}
        />
      </Modal>

      {/* New Payment Modal */}
      <Modal
        closeButton
        show={showPayModal}
        title={`Pay ${displayOrUsername}`}
        onExit={() => setShowPayModal(false)}
      >
        <PaymentModal
          skipProjectTermsStep
          brief={brief}
          pitch={pitch}
          onAccept={(amount) =>
            adhocPayment(brief?.id, pitch?.id, amount * 100)
          }
        />
      </Modal>
    </div>
  );
};

PaymentsTab.propTypes = {
  payments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
      from_amount: PropTypes.number.isRequired,
      from_currency_code: PropTypes.string.isRequired,
      to_amount: PropTypes.number.isRequired,
      to_currency_code: PropTypes.string.isRequired,
      from_twine_fee: PropTypes.number.isRequired,
      from_card_fee: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      from_transaction_status: PropTypes.string,
      timestamp: PropTypes.string.isRequired,
    })
  ),
  stats: PropTypes.shape({
    total_earnings: PropTypes.number,
    total_vaulted: PropTypes.number,
    total_paid_by_client: PropTypes.number,
  }),
  freelancerName: PropTypes.string,
  loading: PropTypes.bool,
  brief: PropTypes.object,
  pitch: PropTypes.object,
  onLoadPayments: PropTypes.func,
};

export default PaymentsTab;
